import { type Animation, createAnimation } from "@ionic/react";
import { TABLET_SHELL_QUERY } from "@/lib/hooks/use-media-query";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const AT_REST = "translateY(0)";

function readToken(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

/** A CSS time in milliseconds: the production build minifies `250ms` to `.25s`. */
export function cssTimeToMs(time: string): number {
  const value = Number.parseFloat(time);
  return time.endsWith("ms") ? value : value * 1000;
}

interface Motion {
  durationToken: string;
  /** The transform the wrapper enters from and leaves to, motion allowing. */
  offset: string;
}

const SHEET: Motion = {
  durationToken: "--duration-sheet",
  offset: "translateY(100%)",
};
/** A panel dropping a few pixels into place under the button it opens from. */
const PANEL: Motion = {
  durationToken: "--duration-enter",
  offset: "translateY(-0.5rem)",
};

/**
 * Both modals move on the motion tokens rather than Ionic's own iOS slide,
 * which is longer and ignores reduced motion; under reduced motion they only
 * fade. `transform` is animated even then: Ionic's iOS stylesheet parks the
 * wrapper a full height down and relies on the enter animation to bring it
 * back.
 */
function modalAnimation(
  baseEl: HTMLElement,
  entering: boolean,
  { durationToken, offset }: Motion
): Animation {
  const root = baseEl.shadowRoot ?? baseEl;
  const reduceMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
  const hidden = reduceMotion ? AT_REST : offset;
  // The backdrop ignores presses while the modal moves, so a double-tap on
  // the button that opened it does not dismiss it again. The anchored panel's
  // `--backdrop-opacity` is 0 (styles/ionic-theme.css), so it stays clear.
  const backdrop = createAnimation()
    .addElement(root.querySelector("ion-backdrop") as HTMLElement)
    .beforeStyles({ "pointer-events": "none" })
    .afterClearStyles(["pointer-events"])
    .fromTo(
      "opacity",
      entering ? "0.01" : "var(--backdrop-opacity)",
      entering ? "var(--backdrop-opacity)" : "0"
    );
  const wrapper = createAnimation()
    .addElement(root.querySelector(".modal-wrapper") as HTMLElement)
    .fromTo("opacity", entering ? "0" : "1", entering ? "1" : "0")
    .fromTo(
      "transform",
      entering ? hidden : AT_REST,
      entering ? AT_REST : hidden
    );
  return createAnimation()
    .addElement(baseEl)
    .duration(
      cssTimeToMs(readToken(reduceMotion ? "--duration-fade" : durationToken))
    )
    .easing(readToken(entering ? "--ease-out" : "--ease-in"))
    .addAnimation([backdrop, wrapper]);
}

function animations(motion: () => Motion) {
  return {
    enterAnimation: (baseEl: HTMLElement) =>
      modalAnimation(baseEl, true, motion()),
    leaveAnimation: (baseEl: HTMLElement) =>
      modalAnimation(baseEl, false, motion()),
  };
}

/** A sheet rising from the bottom edge, as a picker's does. */
export const sheetAnimations = animations(() => SHEET);

/**
 * An `IonModal` with `.header-menu-modal` (styles/ionic-theme.css) is the
 * anchored panel from the tablet shell up and a sheet on a phone. The query is
 * read as each animation starts, so it moves the way the layout then stands.
 */
export const headerMenuAnimations = animations(() =>
  window.matchMedia(TABLET_SHELL_QUERY).matches ? PANEL : SHEET
);
